import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    as?: ElementType
    className?: string
    delay?: number
    id?: string
}

// Fades and lifts its content into place once, the first time it scrolls into view
const Reveal: FC<RevealProps> = ({ children, as: Tag = 'div', className, delay = 0, id }): ReactNode => {
    const ref = useRef<HTMLElement | null>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) return

        if (!('IntersectionObserver' in window)) {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some(entry => entry.isIntersecting)) {
                    setVisible(true)
                    observer.disconnect()
                }
            },
            { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )

        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const style = delay ? ({ '--reveal-delay': `${delay}ms` } as CSSProperties) : undefined

    return (
        <Tag ref={ref} id={id} style={style} className={cn('reveal', visible && 'is-visible', className)}>
            {children}
        </Tag>
    )
}

export default Reveal
